import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { BehaviorSubject, Observable, SubscriptionLike } from 'rxjs';
import { CartItem, Product } from '../models/product.model';
import { PaymentMethod } from '../models/order.model';

// Product code constants
const CARD_CODE_PREFIX = 'CARD_';
const INITIAL_TOPUP_CODE = 'INITIAL_TOPUP';

export interface OrderWizardState {
  currentStep: number;
  cart: CartItem[];
  totalPrice: number;
  paymentCosts: number;
  totalWithCosts: number;
  selectedPaymentMethod: PaymentMethod | null;
  orderId: number | null;
  orderStatus: string;
  isFirstOrder: boolean;
  recipientAddress: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class OrderWizardService {
  private location = inject(Location);
  // Store all products for linked product lookups (e.g., INITIAL_TOPUP for cards)
  private allProducts: Product[] = [];
  // Flag to allow manual INITIAL_TOPUP editing (when user needs to buy top-ups for existing cards)
  private allowManualTopupEdit: boolean = false;
  private stateSubject = new BehaviorSubject<OrderWizardState>({
    currentStep: 1,
    cart: [],
    totalPrice: 0,
    paymentCosts: 0,
    totalWithCosts: 0,
    selectedPaymentMethod: null,
    orderId: null,
    orderStatus: 'pending',
    isFirstOrder: false,
    recipientAddress: null
  });

  public state$: Observable<OrderWizardState> = this.stateSubject.asObservable();

  // Flag to prevent circular updates when handling popstate
  private isHandlingPopState = false;
  // Track if history management is enabled (only when wizard is active)
  private historyEnabled = false;
  // Store the subscription for cleanup
  private locationSubscription: SubscriptionLike | null = null;
  // Base path for the wizard (set when enabled)
  private basePath: string = '';

  constructor() {}

  /**
   * Set the available products list for linked product lookups
   * (e.g., automatically adding INITIAL_TOPUP when a card is added)
   */
  setProducts(products: Product[]): void {
    this.allProducts = products;
  }

  /**
   * Enable/disable manual INITIAL_TOPUP editing mode.
   * When enabled, users can manually add INITIAL_TOPUP to cart
   * (used when user needs to purchase top-ups for previously ordered cards)
   */
  setManualTopupMode(enabled: boolean): void {
    this.allowManualTopupEdit = enabled;
  }

  addToCart(product: Product, quantity: number): void {
    // Prevent manual addition of INITIAL_TOPUP unless in manual topup mode
    if (product.code === INITIAL_TOPUP_CODE && !this.allowManualTopupEdit) {
      return;
    }

    const state = this.getState();
    const existingItem = state.cart.find(item => item.product.id === product.id);

    if (quantity <= 0) {
      // Remove item if quantity is 0 or less
      state.cart = state.cart.filter(item => item.product.id !== product.id);
    } else if (existingItem) {
      existingItem.quantity = quantity;
    } else {
      state.cart.push({ product, quantity });
    }

    // If a card product was added/updated, sync INITIAL_TOPUP quantity (only in normal mode)
    if (this.isCardProduct(product) && !this.allowManualTopupEdit) {
      this.syncInitialTopupWithCards(state);
    }

    this.updateTotalPrice(state);
    this.stateSubject.next(state);
  }

  removeFromCart(productId: number): void {
    const state = this.getState();
    const removedItem = state.cart.find(item => item.product.id === productId);
    const wasCardProduct = removedItem && this.isCardProduct(removedItem.product);

    state.cart = state.cart.filter(item => item.product.id !== productId);

    // If a card product was removed, sync INITIAL_TOPUP quantity
    if (wasCardProduct) {
      this.syncInitialTopupWithCards(state);
    }

    this.updateTotalPrice(state);
    this.stateSubject.next(state);
  }

  updateQuantity(productId: number, quantity: number): void {
    const state = this.getState();
    const item = state.cart.find(item => item.product.id === productId);

    if (item) {
      // Prevent manual modification of INITIAL_TOPUP quantity unless in manual topup mode
      if (item.product.code === INITIAL_TOPUP_CODE && !this.allowManualTopupEdit) {
        return;
      }

      if (quantity <= 0) {
        this.removeFromCart(productId);
      } else {
        const wasCardProduct = this.isCardProduct(item.product);
        item.quantity = quantity;

        // If a card product quantity changed, sync INITIAL_TOPUP (only in normal mode)
        if (wasCardProduct && !this.allowManualTopupEdit) {
          this.syncInitialTopupWithCards(state);
        }

        this.updateTotalPrice(state);
        this.stateSubject.next(state);
      }
    }
  }

  clearCart(): void {
    const state = this.getState();
    state.cart = [];
    state.totalPrice = 0;
    state.paymentCosts = 0;
    state.totalWithCosts = 0;
    this.stateSubject.next(state);
  }

  setOrderId(orderId: number): void {
    const state = this.getState();
    state.orderId = orderId;
    this.stateSubject.next(state);
  }

  setPaymentMethod(method: PaymentMethod): void {
    const state = this.getState();
    state.selectedPaymentMethod = method;
    this.stateSubject.next(state);
  }

  setOrderStatus(status: string): void {
    const state = this.getState();
    state.orderStatus = status;
    this.stateSubject.next(state);
  }

  setFirstOrder(isFirst: boolean): void {
    const state = this.getState();
    state.isFirstOrder = isFirst;
    this.stateSubject.next(state);
  }

  setRecipientAddress(address: string): void {
    const state = this.getState();
    state.recipientAddress = address;
    this.stateSubject.next(state);
  }

  setPaymentCosts(costs: number, totalWithCosts: number): void {
    const state = this.getState();
    state.paymentCosts = costs;
    state.totalWithCosts = totalWithCosts;
    this.stateSubject.next(state);
  }

  goToStep(step: number): void {
    const state = this.getState();
    const previousStep = state.currentStep;
    state.currentStep = step;
    this.stateSubject.next(state);

    // Update URL if not handling a popstate event
    if (!this.isHandlingPopState && this.historyEnabled && step !== previousStep) {
      this.updateUrlForStep(step);
    }
  }

  nextStep(): void {
    const state = this.getState();
    state.currentStep++;
    this.stateSubject.next(state);

    // Update URL if not handling a popstate event
    if (!this.isHandlingPopState && this.historyEnabled) {
      this.updateUrlForStep(state.currentStep);
    }
  }

  previousStep(): void {
    const state = this.getState();
    if (state.currentStep > 1) {
      state.currentStep--;
      this.stateSubject.next(state);

      // Update URL if not handling a popstate event
      if (!this.isHandlingPopState && this.historyEnabled) {
        this.updateUrlForStep(state.currentStep);
      }
    }
  }

  reset(): void {
    this.stateSubject.next({
      currentStep: 1,
      cart: [],
      totalPrice: 0,
      paymentCosts: 0,
      totalWithCosts: 0,
      selectedPaymentMethod: null,
      orderId: null,
      orderStatus: 'pending',
      isFirstOrder: false,
      recipientAddress: null
    });
  }

   /**
   * Enable browser history management for the wizard.
   * Call this when the wizard component initializes.
   */
   enableHistoryManagement(basePath: string): void {
    if (this.historyEnabled) return;

    this.historyEnabled = true;
    this.basePath = basePath;

    // Listen for browser back/forward navigation
    this.locationSubscription = this.location.subscribe((event) => {
      if (event.type === 'popstate') {
        this.handlePopState();
      }
    });

    // Set initial step in URL if not already set
    const currentPath = this.location.path();
    if (!currentPath.includes('step=')) {
      this.updateUrlForStep(1, true);
    } else {
      // Parse step from URL and sync state
      const stepMatch = currentPath.match(/[?&]step=(\d+)/);
      if (stepMatch) {
        const urlStep = parseInt(stepMatch[1], 10);
        if (urlStep >= 1 && urlStep <= 4) {
          this.isHandlingPopState = true;
          const state = this.getState();
          state.currentStep = urlStep;
          this.stateSubject.next(state);
          this.isHandlingPopState = false;
        }
      }
    }
  }

  /**
   * Disable browser history management.
   * Call this when the wizard component is destroyed.
   */
  disableHistoryManagement(): void {
    if (this.locationSubscription) {
      this.locationSubscription.unsubscribe();
      this.locationSubscription = null;
    }
    this.historyEnabled = false;
  }

  getState(clone = true): OrderWizardState {
    const state = this.stateSubject.value;
    return clone ? structuredClone(state) : state;
  }
  
  private updateTotalPrice(state: OrderWizardState): void {
    state.totalPrice = state.cart.reduce((sum, item) =>
      sum + (item.product.price * item.quantity), 0
    );
  }

  /**
   * The initial top-up was discontinued (July 2026): buying a card no longer
   * adds an INITIAL_TOPUP line item to the cart. This now only strips any
   * INITIAL_TOPUP that might still be present (e.g. from restored/legacy state)
   * so cards are always sold without a top-up.
   */
  private syncInitialTopupWithCards(state: OrderWizardState): void {
    state.cart = state.cart.filter(item => item.product.code !== INITIAL_TOPUP_CODE);
  }

  /**
   * Check if a product is a card product (code starts with 'CARD_')
   */
  private isCardProduct(product: Product): boolean {
    return !!product.code && product.code.startsWith(CARD_CODE_PREFIX);
  }

   /**
   * Handle browser back/forward button navigation
   */
   private handlePopState(): void {
    if (!this.historyEnabled) return;

    const currentPath = this.location.path();
    const stepMatch = currentPath.match(/[?&]step=(\d+)/);

    if (stepMatch) {
      const urlStep = parseInt(stepMatch[1], 10);
      const currentStep = this.getState().currentStep;

      // Only update if step actually changed
      if (urlStep !== currentStep && urlStep >= 1 && urlStep <= 4) {
        this.isHandlingPopState = true;
        const state = this.getState();
        state.currentStep = urlStep;
        this.stateSubject.next(state);
        this.isHandlingPopState = false;
      }
    }
  }

  /**
   * Update URL to reflect current step
   */
  private updateUrlForStep(step: number, replaceState: boolean = false): void {
    if (!this.historyEnabled) return;

    const newPath = `${this.basePath}?step=${step}`;

    if (replaceState) {
      this.location.replaceState(newPath);
    } else {
      this.location.go(newPath);
    }
  }
}
